"use client";

import { useEffect } from "react";

const CLOSE_BUTTON_SELECTOR = 'button.klaviyo-close-form, button[aria-label="Close dialog"]';

interface KlaviyoFormsEventDetail {
  type?: string;
}

/**
 * Release focus when the Klaviyo close button is clicked so the outline does
 * not transfer to the minimized teaser, and blur the active element on
 * Klaviyo's native close/submit events so focus does not land on the page.
 */
export function KlaviyoFocusManager(): null {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const closeBtn = target.closest(CLOSE_BUTTON_SELECTOR);
      if (!(closeBtn instanceof HTMLElement)) {
        return;
      }

      closeBtn.blur();
      window.setTimeout(() => {
        const mainContent = document.querySelector("main") || document.body;
        if (mainContent instanceof HTMLElement) {
          mainContent.blur();
        }
      }, 50);
    };

    const onKlaviyoForms = (event: Event) => {
      const detail = (event as CustomEvent<KlaviyoFormsEventDetail>).detail;
      if (detail && (detail.type === "close" || detail.type === "submit")) {
        const active = document.activeElement;
        if (active instanceof HTMLElement) {
          active.blur();
        }
      }
    };

    document.addEventListener("click", onClick);
    window.addEventListener("klaviyoForms", onKlaviyoForms);
    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("klaviyoForms", onKlaviyoForms);
    };
  }, []);

  return null;
}
